import {
  darfStarten,
  fuerAndereVorgemerkt,
  GROESSE_LABELS,
  sofortNurFuerVorgesehene,
  groesseVon,
  ZEITFENSTER_KURZ,
  type TaskSource,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useEffect, useState } from 'react'
import { useMerker } from '../lib/merker'
import { useNehmen, useStarten } from '../lib/mutationen'
import type { Pool } from '../lib/queries'
import type { Naechste } from './meintag/lage'
import { AufgabenVerweis } from './AufgabeKontext'
import { Button, PrioBadge, SourceBadge } from './ui/primitives'
import { AufgabenWeltMarke } from './WorkspaceMarke'
import { WiedervorlageMarke } from './meintag/WiedervorlageMarke'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * **Eine Rangfolge, zwei Handgriffe** ([Z-06](../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * Hieß bis zum 24.08.2026 „Aufgabe ziehen" und zeigte nur den Vorrat. Daneben stand links eine
 * flache Liste der eigenen Aufgaben – ohne Sortierung, ohne die drei Fakten, mit einem anderen
 * Knopf. Nils: *„Ich denke es ist für einen Entwickler noch sehr schwierig zu unterscheiden,
 * wann er Aufgaben ziehen soll und wann er auf seinen Vorrat zugreift."*
 *
 * Jetzt steht beides in **einer** Liste, und die Marke an der Zeile beantwortet die Frage, die
 * der Entwickler sonst selbst stellen musste:
 *
 * | Marke | heißt | Knöpfe |
 * |---|---|---|
 * | ● | gehört dir (Work führt dich) | **Starten** |
 * | ★ | für dich vorgemerkt | **Nehmen** · Starten |
 * | – | herrenlos | **Nehmen** · Starten |
 *
 * **Nehmen ist nie gesperrt, Starten schon.** Bei vollem Fokus bleibt „Nehmen" klickbar – wer
 * sich versorgen will, soll das dürfen; wer noch etwas anfangen will, nicht (`darfStarten`).
 *
 * Vor dem Klick sind drei Fragen beantwortet – wie wichtig (Pool **und** Priorität), wann dran
 * (Zeitfenster mit Datum), wie groß –, dazu die Zeile „kommen in Frage" mit allen Namen und
 * „Danach käme": die nächsten Plätze, damit man die eigentliche Frage hinter jedem Vorschlag
 * beantworten kann – *ist das wirklich das Wichtigste?*
 */

/** Wie viele Plätze der Warteschlange die Vorschau zeigt. */
const DANACH = 3

/** Der eigene Reiter für das, was mir schon gehört – kein Pool, deshalb eine eigene Kennung. */
const REITER_EIGENE = 'bei-dir'

export function WasAlsNaechstes({
  rangfolge,
  pools,
  ichId,
  wip,
  wipLimit,
  hinweis,
  schliessen,
}: {
  rangfolge: readonly Naechste[]
  pools: readonly Pool[]
  ichId: string | null
  wip: number
  wipLimit: number
  hinweis: string | null
  schliessen: () => void
}) {
  const nehmen = useNehmen()
  const starten = useStarten()
  const [reiter, setReiter] = useState<string>('alle')
  const [i, setI] = useState(0)
  /**
   * **Die ganze Liste bleibt offen, wenn man sie offen haben will** (01.10.2026).
   *
   * Christian Hunhoff: *„mich dann nur «Was ist als Nächstes» durchzuklicken finde ich etwas
   * unpraktisch"* – und: er sehe nur die großen Aufgaben. Beides zeigt auf denselben Griff: Die
   * ganze Liste **gibt** es seit dem 25.08., nur fing jedes Öffnen des Fensters wieder mit einem
   * einzelnen Vorschlag an. Wer sie lieber als Liste liest, klickt sie jedes Mal neu auf.
   *
   * Gemerkt wird je **Gerät** (`useMerker`, `localStorage`) und nicht am Konto: das ist eine
   * Ansichtsvorliebe wie die Filterwahl im Tages-Popup – am Telefon will man die Liste eher
   * nicht, am Schreibtisch schon.
   */
  const [listeMerker, setListeMerker] = useMerker<'an' | 'aus'>('naechstes-liste', 'aus', [
    'an',
    'aus',
  ])
  const alleZeigen = listeMerker === 'an'
  const setAlleZeigen = (an: boolean) => setListeMerker(an ? 'an' : 'aus')
  /**
   * **Fremd Vorgemerktes liegt hinter einem Klick** (Nils, 24.08.2026: *„in 99 % der Fälle fällt
   * jemand nur spontan kurz aus, wo der Leiter die dann verteilt … sonst ein Wust von Aufgaben,
   * die er in 1 % der Zeit nur sehen muss"*).
   *
   * Es zu **sortieren** reichte nicht: hinten steht es trotzdem im Strom, zählt im Zähler mit
   * und wird durchgeblättert. Weggenommen wird es aber auch nicht – „Vorrang, kein Anspruch"
   * (E-87) heißt, dass Einspringen möglich bleiben muss.
   */
  const [fremdeZeigen, setFremdeZeigen] = useState(false)

  const imReiter =
    reiter === 'alle'
      ? rangfolge
      : reiter === REITER_EIGENE
        ? rangfolge.filter((a) => a.marke === 'bei_dir')
        : rangfolge.filter((a) => a.pool?.id === reiter)
  const fremde = imReiter.filter((a) => fuerAndereVorgemerkt(a, ichId))
  const offen = imReiter.filter((a) => !fuerAndereVorgemerkt(a, ichId))

  /**
   * Zwei Rückfalllösungen, in dieser Reihenfolge:
   *
   * 1. **Bleibt ohne die fremden nichts übrig, werden sie gezeigt** – sonst stünde „es wartet
   *    nichts" neben sechzehn Aufgaben, und das wäre schlicht gelogen.
   * 2. **Leerer Reiter: zurück auf „Alle"**, statt einen Vorschlag aus einem anderen Vorrat zu
   *    zeigen, ohne dass der Reiter es sagt.
   */
  const strom = fremdeZeigen || offen.length === 0 ? imReiter : offen
  const liste = strom.length > 0 ? strom : rangfolge
  const kandidat = liste[i % Math.max(liste.length, 1)]

  useEffect(() => setI(0), [reiter, fremdeZeigen])

  /* Tastatur: → blättert, 1–9 wechselt den Reiter, Esc schließt. */
  useEffect(() => {
    const auf = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'Escape') return schliessen()
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        setI((n) => n + 1)
      }
      if (/^[1-9]$/.test(e.key)) {
        const alle = ['alle', REITER_EIGENE, ...pools.map((p) => p.id)]
        const ziel = alle[Number(e.key) - 1]
        if (ziel) {
          e.preventDefault()
          setReiter(ziel)
        }
      }
    }
    document.addEventListener('keydown', auf)
    return () => document.removeEventListener('keydown', auf)
  }, [pools, schliessen])

  if (!kandidat) {
    return (
      <Rahmen schliessen={schliessen}>
        <p className="py-4 text-center text-[13px] text-ink2">
          Nichts offen und nichts im Vorrat. Der Parkplatz zählt nicht mit – aus einem Regal wird
          nicht gezogen.
        </p>
      </Rahmen>
    )
  }

  const eigen = kandidat.marke === 'bei_dir'
  const pruefung = darfStarten({
    inArbeit: wip,
    wipLimit,
    poolDurchbrichtWip: kandidat.pool?.wipDurchbrechen ?? false,
  })
  const wunsch = kandidat.wunschIds
  const fuerMich = ichId !== null && wunsch.includes(ichId)
  const fremderWunsch = wunsch.length > 0 && !fuerMich
  const istSofort = kandidat.pool?.wipDurchbrechen ?? false
  /** Im Sofort-Pool ist „kommen in Frage" ein Anspruch (E-346) – dann gibt es keinen Knopf. */
  const nurFuerAndere = sofortNurFuerVorgesehene(
    { wunschIds: wunsch, poolDurchbrichtWip: istSofort },
    ichId,
  )
  const groesse = groesseVon(kandidat)

  /**
   * **Vorrang, kein Anspruch** (Nils, 04.08.2026): *„jeder soll sich die trotzdem holen können,
   * dann mit einer Abfrage – Grund zwingend."*
   *
   * Die Rückfrage kommt hier und nicht erst vom Server: eine Aufgabe, die nach dem Klick
   * zurückspringt, weil eine Begründung fehlt, sieht wie ein Fehler aus. Der Server verlangt sie
   * trotzdem – die Oberfläche ist die Erklärung, nicht die Sicherung.
   *
   * Ein `null` heißt **abgebrochen**; `undefined` heißt „es gab nichts zu fragen".
   */
  const grundHolen = (): string | null | undefined => {
    if (!fremderWunsch) return undefined
    const grund = window.prompt(
      `${kandidat.wunschNamen} ${wunsch.length === 1 ? 'kommt' : 'kommen'} für diese Aufgabe in Frage.\n\n` +
        'Du kannst sie trotzdem nehmen – warum?',
      '',
    )
    return grund?.trim() ? grund.trim() : null
  }

  const handgriff = (was: 'nehmen' | 'starten') => () => {
    const grund = grundHolen()
    if (grund === null) return
    const eingabe = grund ? { id: kandidat.id, grund } : { id: kandidat.id }
    if (was === 'nehmen') nehmen.mutate(eingabe)
    else starten.mutate(eingabe)
    schliessen()
  }

  const danach = liste.slice((i % liste.length) + 1, (i % liste.length) + 1 + DANACH)
  const fehler = nehmen.error ?? starten.error

  return (
    <Rahmen
      schliessen={schliessen}
      zaehler={`Vorschlag ${(i % liste.length) + 1} von ${liste.length}`}
    >
      {/**
       * **Die Reiter sind der erste Griff, nicht ein Filter am Ende.** Wer weiß, dass er heute
       * nur Kleinkram schafft, springt in die Tickets. „Bei dir" steht als erster Pool-Reiter,
       * weil es der einzige ist, hinter dem nichts mehr zu entscheiden ist – nur noch anfangen.
       * Der Parkplatz steht bewusst *nicht* dabei: `nurVorraete` hat ihn aussortiert, und ein
       * grauer Knopf, der nie geht, ist ein Rätsel statt einer Auskunft.
       */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        <Reiter
          an={reiter === 'alle'}
          zahl={rangfolge.filter((a) => !fuerAndereVorgemerkt(a, ichId)).length}
          onClick={() => setReiter('alle')}
          titel="Sofort geht vor, dann was dir gehört, dann der Vorrat."
        >
          Alle
        </Reiter>
        {(() => {
          const n = rangfolge.filter((a) => a.marke === 'bei_dir').length
          return (
            <Reiter
              an={reiter === REITER_EIGENE}
              zahl={n}
              leer={n === 0}
              stil="eigen"
              onClick={() => n > 0 && setReiter(REITER_EIGENE)}
              titel="Gehört dir schon – hier wird nur noch angefangen."
            >
              ● Bei dir
            </Reiter>
          )
        })()}
        {pools.map((p) => {
          /* Die Zahl am Reiter zählt, was **angeboten** wird – sonst verspricht sie mehr,
             als hinter dem Klick steht. Die fremd Vorgemerkten stehen in ihrer eigenen Zeile. */
          const n = rangfolge.filter(
            (a) => a.pool?.id === p.id && !fuerAndereVorgemerkt(a, ichId),
          ).length
          return (
            <Reiter
              key={p.id}
              an={reiter === p.id}
              zahl={n}
              leer={n === 0}
              stil={p.wipDurchbrechen ? 'sofort' : undefined}
              onClick={() => n > 0 && setReiter(p.id)}
              titel={p.regeln ?? undefined}
            >
              {p.icon} {p.label}
            </Reiter>
          )
        })}
      </div>

      {/**
       * **Der Klick, hinter dem die fremd Vorgemerkten liegen** (Nils, 24.08.2026).
       *
       * Mit Zahl, damit es keine verborgene Auskunft ist: man sieht, dass es sie gibt, ohne sie
       * täglich mitzublättern. Steht nur da, wenn es welche gibt – und nicht, wenn ohnehin schon
       * alles gezeigt wird, weil sonst nichts übrig bliebe.
       */}
      {fremde.length > 0 && offen.length > 0 && (
        <button
          type="button"
          onClick={() => setFremdeZeigen((f) => !f)}
          aria-pressed={fremdeZeigen}
          className="mb-3 cursor-pointer text-[12px] text-muted hover:text-ink"
        >
          {fremdeZeigen
            ? `★ ${fremde.length} für andere vorgemerkte wieder ausblenden`
            : `★ ${fremde.length} sind für andere vorgemerkt – trotzdem zeigen`}
        </button>
      )}
      {fremde.length > 0 && offen.length === 0 && (
        <p className="mb-3 text-[12px] text-muted">
          ★ Alles hier ist für andere vorgemerkt.{' '}
          {istSofort
            ? 'Sofort-Aufgaben nehmen nur die Vorgesehenen.'
            : 'Nehmen darfst du trotzdem – mit Grund.'}
        </p>
      )}

      <div
        className={`rounded-xl border p-3.5 ${
          istSofort
            ? 'border-transparent border-l-[3px] border-l-pool-sofort bg-pool-sofort-bg'
            : eigen
              ? 'border-transparent border-l-[3px] border-l-accent bg-page'
              : 'border-line bg-page'
        }`}
      >
        {/**
         * **Steht über dem Titel, weil es vor dem Titel gilt** (Nils, 21.08.2026: *„sofort ist
         * immer das wichtigste (bug, fehler usw.)"*, [E-209](../../../docs/ENTSCHEIDUNGEN.md)).
         */}
        {istSofort && (
          <div className="mb-2 text-[11px] font-bold tracking-[0.04em] text-pool-sofort uppercase">
            {kandidat.pool?.icon} {kandidat.pool?.label} · geht vor allem anderen
          </div>
        )}
        {eigen && (
          <div className="mb-2 text-[11px] font-bold tracking-[0.04em] text-accent-deep uppercase">
            ● gehört dir · nur noch anfangen
          </div>
        )}

        <AufgabenVerweis
          aufgabeId={kandidat.id}
          className="mb-2 block cursor-pointer text-left text-base leading-tight font-semibold hover:text-accent hover:underline"
        >
          {kandidat.title}
        </AufgabenVerweis>

        <div className="flex flex-wrap items-center gap-1.5">
          <SourceBadge source={kandidat.sourceId as TaskSource} label={kandidat.externalKey} />
          {/** Z-07.8 – der Vorschlag kann aus jeder Arbeitswelt kommen, in der ich Mitglied bin. */}
          <AufgabenWeltMarke workspaceId={kandidat.workspaceId} />
          {kandidat.vorhabenLabel ? (
            <Marke stil="vorhaben">◆ {kandidat.vorhabenLabel}</Marke>
          ) : (
            <Marke>ohne Vorhaben</Marke>
          )}
          {kandidat.inPools > 1 && (
            <Marke titel="Genommen verschwindet sie aus allen.">in {kandidat.inPools} Pools</Marke>
          )}
        </div>

        {/* Die drei Fragen, die vor dem Griff beantwortet sein müssen. */}
        <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden rounded-[10px] border border-line bg-line sm:grid-cols-3">
          <Fakt titel="Wie wichtig">
            <span className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold">
              {kandidat.pool ? (
                <Marke stil={istSofort ? 'sofort' : 'pool'}>
                  {kandidat.pool.icon} {kandidat.pool.label}
                </Marke>
              ) : (
                <Marke stil="eigen">● gehört dir</Marke>
              )}
              {kandidat.wiedervorlage && <WiedervorlageMarke tag={kandidat.wiedervorlage} />}
              {kandidat.priority && <PrioBadge priority={kandidat.priority} />}
            </span>
            <small className="mt-[3px] block text-[11.5px] text-muted">
              {istSofort
                ? 'Der Pool schlägt die Priorität – auch ein C ginge hier vor einem A aus den Tickets.'
                : /**
                   * Was mir gehört, hat keinen Pool mehr – die Zugehörigkeit endet beim Nehmen.
                   * Der Satz sagt das, statt ein leeres Feld zu zeigen.
                   */
                  (kandidat.pool?.regeln ??
                  'Liegt in keinem Vorrat mehr – du hast sie oder sie wurde dir zugeteilt.')}
            </small>
          </Fakt>

          {/**
           * **„Wann dran" beantwortet der wirksame Termin, nicht das Zeitfenster**
           * ([E-308](../../../../docs/ENTSCHEIDUNGEN.md#e-308), 02.09.2026).
           *
           * Nils: *„Im Vorlauf soll bei allen Sachen das errechnete Fällig stehen. Nicht nur bei
           * der akutesten (in Mein Tag und beim Ziehen)."*
           *
           * Hier stand `zeitfensterStand(…)` – also genau die Rechnung, die
           * [E-281](../../../../docs/ENTSCHEIDUNGEN.md#e-281) am 31.08.2026 in „Mein Tag"
           * abgelöst hat. Eine Aufgabe mit gesetzter **Fälligkeit** und ohne Zeitfenster stand
           * im Ziehen-Fenster als „nicht eingeordnet" da, während dieselbe Aufgabe eine Ansicht
           * weiter „2 Tage überfällig" trug.
           *
           * Die Einstufung bleibt trotzdem stehen: sie sagt, **wie verbindlich** geplant wurde,
           * das Datum sagt **wann**. Und die Herkunft steht ausgeschrieben darunter – hier ist
           * Platz dafür, anders als an einer Listenzeile.
           */}
          <Fakt titel="Wann dran">
            <span className="text-[13px] font-semibold">
              {kandidat.frist ? (
                <span
                  className={
                    kandidat.frist.stufe === 'ueberfaellig'
                      ? 'text-pool-sofort'
                      : kandidat.frist.stufe === 'heute'
                        ? 'text-warn'
                        : undefined
                  }
                >
                  {kandidat.frist.text}
                </span>
              ) : (
                'kein Termin'
              )}
              {kandidat.zeitfenster && (
                <span className="font-normal text-muted">
                  {' '}
                  · {ZEITFENSTER_KURZ[kandidat.zeitfenster as Zeitfenster]}
                </span>
              )}
            </span>
            <small className="mt-[3px] block text-[11.5px] text-muted">
              {kandidat.frist === null
                ? 'kein Zeitfenster gesetzt – sortiert in der Mitte'
                : (kandidat.fristHerkunft ?? 'eigene Fälligkeit')}
            </small>
          </Fakt>

          <Fakt titel="Wie groß">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold">
              <Mass stufe={groesse} />
              {GROESSE_LABELS[groesse]}
            </span>
            <small className="mt-[3px] block text-[11.5px] text-muted">
              {kandidat.groesse === null
                ? 'nicht bewertet – gilt als mittel'
                : 'von Hand eingeschätzt'}
            </small>
          </Fakt>
        </div>

        {/* Für wen ist sie vorgesehen – und bin ich der Einzige? Bei Eigenem beantwortet. */}
        {!eigen && (
          <Wunschzeile
            namen={kandidat.wunschNamen}
            fuerMich={fuerMich}
            anzahl={wunsch.length}
            nurFuerAndere={nurFuerAndere}
          />
        )}
      </div>

      {/**
       * **Zwei Knöpfe, und der wichtigere steht vorn.**
       *
       * Bei Eigenem gibt es nichts mehr zu nehmen – dann ist „Starten" der Hauptknopf. Im Vorrat
       * führt „Nehmen", weil das der Handgriff ist, der auch bei vollem Fokus geht; „Starten"
       * steht daneben und ist gesperrt, wenn kein Platz frei ist.
       */}
      <div className="mt-3.5 flex flex-wrap gap-2">
        {eigen ? (
          <Button
            variant="primary"
            className="flex-1 justify-center"
            disabled={!pruefung.erlaubt}
            onClick={handgriff('starten')}
            {...(pruefung.grund ? { title: pruefung.grund } : {})}
          >
            Starten
          </Button>
        ) : nurFuerAndere ? (
          <p className="flex-1 rounded-[9px] bg-page px-3 py-2 text-[12.5px] text-ink2">
            Nur {kandidat.wunschNamen} {wunsch.length === 1 ? 'kann' : 'können'} diese
            Sofort-Aufgabe nehmen. Wenn du einspringen sollst, lässt dich die Leitung unter „kommen
            in Frage" eintragen.
          </p>
        ) : (
          <>
            <Button
              variant="primary"
              className="flex-1 justify-center"
              onClick={handgriff('nehmen')}
            >
              {fremderWunsch ? 'Trotzdem nehmen' : 'Nehmen'}
            </Button>
            <Button
              variant="ghost"
              disabled={!pruefung.erlaubt}
              onClick={handgriff('starten')}
              {...(pruefung.grund ? { title: pruefung.grund } : {})}
            >
              Starten
            </Button>
          </>
        )}
        <Button variant="ghost" onClick={() => setI((n) => n + 1)} disabled={liste.length < 2}>
          Nächster ›
        </Button>
        <Button variant="ghost" onClick={() => setAlleZeigen(!alleZeigen)}>
          {alleZeigen ? 'Liste zuklappen' : 'Ganze Liste zeigen'}
        </Button>
      </div>

      {!pruefung.erlaubt && (
        <p role="alert" className="mt-2 text-[12px] font-semibold text-warn">
          {pruefung.grund}
        </p>
      )}
      {hinweis && <p className="mt-2 text-[12px] text-muted">{hinweis}</p>}
      {fehler && (
        <p
          role="alert"
          className="mt-2 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12.5px] text-warn"
        >
          {fehler.message}
        </p>
      )}

      {/**
       * **„Danach käme"** beantwortet die Frage hinter jedem Vorschlag – *ist das wirklich das
       * Wichtigste?* – ohne die Liste zu wechseln. Ein Klick springt hin.
       */}
      {danach.length > 0 && (
        <div className="mt-3.5">
          <div className="mb-1 text-[9.5px] font-semibold tracking-[0.09em] text-muted uppercase">
            Danach käme
          </div>
          {danach.map((a, n) => (
            <Kurzzeile
              key={a.id}
              aufgabe={a}
              rang={(i % liste.length) + n + 2}
              ichId={ichId}
              onClick={() => setI((i % liste.length) + n + 1)}
            />
          ))}
        </div>
      )}

      {alleZeigen && (
        <div className="mt-3 border-t border-line pt-2">
          {liste.map((a, n) => (
            <Kurzzeile key={a.id} aufgabe={a} rang={n + 1} ichId={ichId} onClick={() => setI(n)} />
          ))}
        </div>
      )}

      {/**
       * **Die Erklärzeile sagt genau das, was `poolVergleicher` tut** (01.10.2026).
       *
       * Christian Hunhoff hatte gefragt, ob die Reihenfolge nach der **Größe** der Aufgaben
       * geht – sie tut es nicht, die Größe steht im Vergleicher an keiner Stelle. Beim Nachsehen
       * fiel auf, dass diese Zeile trotzdem etwas Falsches behauptete, und zwar seit zwei
       * Änderungen:
       *
       * | hier stand | tatsächlich |
       * |---|---|
       * | Zeitfenster **vor** ★ | ★ steht **über** der Zeitfenster-Stufe – der Stern ist die
       *   Planungsentscheidung, nicht eine Meinung über die Person (E-209, nachgeschärft 24.08.) |
       * | kein Termin | seit [E-381](../../../../docs/ENTSCHEIDUNGEN.md#e-381) entscheidet bei
       *   gleicher Stufe der **Termin**, erst danach das Alter |
       *
       * Die Ausnahme über dem Stern steht jetzt auch dran: Was **überfällig oder heute fällig**
       * und nicht für jemand anderen vorgemerkt ist, geht vor – Nils, 24.08.2026: *„Ein
       * herrenloses JETZT oder Pool Sofort sollte weiter Prio haben."*
       *
       * Eine Legende, die etwas anderes sagt als der Code, ist schlimmer als keine: Wer die
       * Reihenfolge nicht versteht, glaubt dann, er habe ein Feld falsch gesetzt.
       */}
      <p className="mt-3 text-[11.5px] leading-relaxed text-muted">
        Reihenfolge: <b className="text-pool-sofort">🔥 Sofort</b> →{' '}
        <b className="text-accent-deep">● was dir gehört</b> → <b className="text-ink">Vorrat</b>;
        darin <b className="text-ink">überfällig/heute</b> → <b className="text-ink">★ für wen</b> →{' '}
        <b className="text-ink">Zeitfenster</b> → <b className="text-ink">Priorität</b> →{' '}
        <b className="text-ink">Termin</b> → <b className="text-ink">Alter</b>. Die{' '}
        <b className="text-ink">Größe</b> sortiert nicht – sie steht nur dabei.
        <br />
        <b className="text-ink">Nehmen</b> heißt „gehört mir" und ist nie gesperrt.{' '}
        <b className="text-ink">Starten</b> heißt „ich mache das jetzt"
        {/* Z-07.13 – im persönlichen Workspace gibt es keine Plätze, die man belegen könnte. */}
        {Number.isFinite(wipLimit)
          ? ` und belegt einen der ${wipLimit} Fokus-Plätze.`
          : ' – hier ohne Limit.'}{' '}
        🔥 Sofort darf beides trotzdem. 🅿️ Parkplatz wird nie angeboten.
      </p>
    </Rahmen>
  )
}

/* ── Bausteine ─────────────────────────────────────────────────────────── */

function Rahmen({
  children,
  schliessen,
  zaehler,
}: {
  children: React.ReactNode
  schliessen: () => void
  zaehler?: string
}) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-center justify-center bg-[rgb(23_22_20/0.38)] p-3 sm:p-5"
      {...hintergrundSchliesst(schliessen)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Was als Nächstes"
        className="max-h-full w-[min(720px,100%)] overflow-y-auto rounded-card border border-line2 bg-surface p-5 shadow-card"
      >
        <div className="mb-3.5 flex items-baseline gap-2.5">
          <h4 className="text-[17px] font-bold">Was als Nächstes</h4>
          {zaehler && <span className="tabular text-[11.5px] text-muted">{zaehler}</span>}
          <button
            type="button"
            onClick={schliessen}
            aria-label="Schließen"
            className="ml-auto cursor-pointer text-[13px] text-muted hover:text-ink"
          >
            ✕
          </button>
        </div>
        {children}
        <p className="mt-2 text-[11.5px] text-muted">
          <Taste>→</Taste> nächster · <Taste>1</Taste>–<Taste>9</Taste> Reiter · <Taste>Esc</Taste>{' '}
          schließt
        </p>
      </div>
    </div>
  )
}

function Taste({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded border border-line2 bg-page px-[5px] py-px text-[10.5px]">
      {children}
    </span>
  )
}

function Reiter({
  an,
  zahl,
  leer,
  stil,
  titel,
  onClick,
  children,
}: {
  an: boolean
  zahl: number
  leer?: boolean | undefined
  stil?: 'sofort' | 'eigen' | undefined
  titel?: string | undefined
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={an}
      disabled={leer}
      {...(titel ? { title: titel } : {})}
      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] border px-2.5 py-[5px] text-xs font-semibold ${
        an
          ? stil === 'sofort'
            ? 'border-transparent bg-pool-sofort-bg text-pool-sofort'
            : stil === 'eigen'
              ? 'border-transparent bg-accent-soft text-accent-deep'
              : 'border-transparent bg-ink text-white'
          : 'border-line2 text-ink2 hover:bg-page'
      } ${leer ? 'cursor-not-allowed opacity-40' : ''}`}
    >
      {children} <span className="tabular text-[11px] font-normal opacity-75">{zahl}</span>
    </button>
  )
}

function Fakt({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 bg-surface px-2.5 py-2.5">
      <span className="mb-1.5 block text-[9.5px] tracking-[0.09em] text-muted uppercase">
        {titel}
      </span>
      {children}
    </div>
  )
}

function Marke({
  children,
  stil,
  titel,
}: {
  children: React.ReactNode
  stil?: 'vorhaben' | 'sofort' | 'pool' | 'eigen'
  titel?: string
}) {
  const klasse =
    stil === 'vorhaben'
      ? 'bg-vorhaben-bg text-pool-projekte border-transparent font-semibold'
      : stil === 'sofort'
        ? 'bg-pool-sofort-bg text-pool-sofort border-transparent font-semibold'
        : stil === 'eigen'
          ? 'bg-accent-soft text-accent-deep border-transparent font-semibold'
          : stil === 'pool'
            ? 'bg-pool-tickets-bg text-pool-tickets border-transparent'
            : 'bg-page text-ink2 border-line'
  return (
    <span
      {...(titel ? { title: titel } : {})}
      className={`rounded-md border px-[7px] py-[2px] text-[10.5px] whitespace-nowrap ${klasse}`}
    >
      {children}
    </span>
  )
}

/** klein/mittel/groß als drei Balken – eine Zahl wäre genauer, als die Angabe es ist. */
function Mass({ stufe }: { stufe: 'klein' | 'mittel' | 'gross' }) {
  const an = stufe === 'klein' ? 1 : stufe === 'gross' ? 3 : 2
  return (
    <span className="inline-flex gap-px" aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <i key={n} className={`block h-3 w-2 rounded-sm ${n <= an ? 'bg-ink2' : 'bg-line2'}`} />
      ))}
    </span>
  )
}

/**
 * **Wer kommt in Frage – und bin ich der Einzige?**
 *
 * Nils, 21.08.2026: *„bei den Aufgaben ziehen sollten noch, wenn mehrere da, die
 * «Wunsch-Entwickler» stehen, wenn man das nicht selber nur ist, damit man sieht, dass die auch
 * anderen angezeigt wird."*
 *
 * Vier Fälle, vier Töne. Der dritte ist der, um den es geht: ohne diese Zeile wundert man sich
 * am Nachmittag, warum die Aufgabe weg ist.
 */
function Wunschzeile({
  namen,
  fuerMich,
  anzahl,
  nurFuerAndere = false,
}: {
  namen: string | null
  fuerMich: boolean
  anzahl: number
  /** Sofort-Pool mit Vorgesehenen, und ich gehöre nicht dazu (E-346). */
  nurFuerAndere?: boolean
}) {
  if (anzahl === 0) {
    return (
      <div className="mt-2.5 flex items-start gap-2.5 rounded-[9px] border border-line bg-page px-2.5 py-2.5 text-[12.5px]">
        <span className="shrink-0 text-sm">☆</span>
        <span>
          <b className="text-ink">Für niemanden vorgesehen.</b>
          <small className="mt-0.5 block text-[11.5px] text-muted">
            Steht bei allen gleich weit oben – wer zuerst nimmt, hat sie.
          </small>
        </span>
      </div>
    )
  }

  const andere = anzahl - (fuerMich ? 1 : 0)
  return (
    <div
      className={`mt-2.5 flex items-start gap-2.5 rounded-[9px] border border-transparent px-2.5 py-2.5 text-[12.5px] ${
        fuerMich ? 'bg-good-bg' : 'bg-pool-sofort-bg'
      }`}
    >
      <span className={`shrink-0 text-sm ${fuerMich ? 'text-good-text' : 'text-pool-sofort'}`}>
        ★
      </span>
      <span>
        {fuerMich && andere === 0 && (
          <>
            <b className="text-ink">Nur für dich vorgesehen.</b>
            <small className="mt-0.5 block text-[11.5px] text-muted">
              Sie steht in deiner Liste oben und wird sonst niemandem bevorzugt angeboten.
            </small>
          </>
        )}
        {fuerMich && andere > 0 && (
          <>
            <b className="text-ink">
              Für dich und {andere} weitere: {namen}
            </b>
            <small className="mt-0.5 block text-[11.5px] text-muted">
              {andere === 1 ? 'Er oder sie sieht sie' : 'Sie sehen sie'} genauso weit oben im Vorrat
              – wer zuerst nimmt, hat sie. Vorrang, kein Anspruch.
            </small>
          </>
        )}
        {!fuerMich && (
          <>
            <b className="text-ink">Vorgesehen für {namen} – nicht für dich.</b>
            <small className="mt-0.5 block text-[11.5px] text-muted">
              {nurFuerAndere
                ? 'Im Sofort-Pool ist das ein Anspruch: niemand sonst nimmt sie.'
                : 'Vorrang, kein Anspruch: du kannst sie trotzdem nehmen, Work fragt dann nach dem Grund.'}
            </small>
          </>
        )}
      </span>
    </div>
  )
}

function Kurzzeile({
  aufgabe,
  rang,
  ichId,
  onClick,
}: {
  aufgabe: Naechste
  rang: number
  ichId: string | null
  onClick: () => void
}) {
  const fuerMich = ichId !== null && aufgabe.wunschIds.includes(ichId)
  return (
    <button
      type="button"
      onClick={onClick}
      /* Haken für die Tests – die Reihenfolge ist die Zusicherung aus E-209 und Z-06, und sie
         über den sichtbaren Text zu rekonstruieren scheitert an Emoji und Marken in derselben
         Zeile. */
      data-vorschlag={rang}
      data-titel={aufgabe.title}
      data-marke={aufgabe.marke}
      className="flex w-full cursor-pointer items-center gap-2 border-b border-line py-1.5 text-left text-[12.5px] last:border-b-0 hover:text-accent-deep"
    >
      <span className="tabular w-4 shrink-0 text-[11px] text-muted">{rang}.</span>
      {/** Ohne Pool und nicht „bei dir": von der Quelle zugeschrieben (E-316). */}
      <span
        className="shrink-0"
        title={
          aufgabe.pool?.label ??
          (aufgabe.marke === 'bei_dir' ? 'gehört dir' : 'dir von der Quelle zugeschrieben')
        }
      >
        {aufgabe.marke === 'bei_dir' ? '●' : (aufgabe.pool?.icon ?? '◐')}
      </span>
      <span className="min-w-0 flex-1 truncate" title={aufgabe.title}>
        {aufgabe.title}
      </span>
      {aufgabe.wiedervorlage && <WiedervorlageMarke tag={aufgabe.wiedervorlage} />}
      {aufgabe.wunschIds.length > 0 && (
        <span
          className="shrink-0 text-[11px] text-muted"
          title={`Kommen in Frage: ${aufgabe.wunschNamen}`}
        >
          ★ {fuerMich ? 'du' : (aufgabe.wunschNamen ?? '').split(',')[0]?.trim().split(' ')[0]}
          {aufgabe.wunschIds.length > 1 && ` +${aufgabe.wunschIds.length - 1}`}
        </span>
      )}
      {/**
       * **Auch hier der wirksame Termin** (E-308). Die Spalte trug bis zum 02.09.2026 die
       * Einstufung („10 T") und ein ⚠, wenn deren Frist durch war – eine gerissene
       * **Fälligkeit** sah man daran nie.
       */}
      <span
        className="shrink-0 text-[11px] text-muted"
        title={aufgabe.fristHerkunft ? `Termin ${aufgabe.fristHerkunft}` : undefined}
      >
        {aufgabe.frist ? (
          <span
            className={
              aufgabe.frist.stufe === 'ueberfaellig' ? 'font-semibold text-pool-sofort' : undefined
            }
          >
            {aufgabe.frist.text}
          </span>
        ) : (
          '–'
        )}
      </span>
      {aufgabe.priority && <PrioBadge priority={aufgabe.priority} />}
      <span className="w-12 shrink-0 text-right text-[11px] text-muted">
        {GROESSE_LABELS[groesseVon(aufgabe)]}
      </span>
    </button>
  )
}
